import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileCheck2, ShieldCheck, Truck } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import MarketingArt from "../../components/marketing/MarketingArt.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import RichText from "../../components/marketing/RichText.jsx";
import { ROUTE_META, PRICE_FROM, SITE } from "../../seo/siteMeta.js";
import { aboutPageSchema, breadcrumbSchema, faqSchema } from "../../seo/schema.js";
import { IRS_EFILE } from "../../data/compliance.js";
import { SUPPORT_EMAIL, SUPPORT_PHONE_DISPLAY } from "../../components/ui/HelpSupportModal/HelpSupportModal.jsx";

const POINTS = [
  {
    icon: Truck,
    title: "Built around the form",
    body: "Every step follows Form 2290 and Schedule 1 in order — tax preparer, business, trucks, tax summary, review, pay, sign & submit.",
  },
  {
    icon: FileCheck2,
    title: "Real IRS rate tables",
    body: "Tax is computed from the published TY2026 tables for all 21 weight categories, standard and logging, full-period and partial-period.",
  },
  {
    icon: ShieldCheck,
    title: "Three ways to work",
    body: "Switch between Daylight, Night Haul, and Dispatch themes — same numbers, same flow, whichever suits the cab or the office.",
  },
];

// Brand questions (2026-10-22). This page is where Google should land for
// "easyfile2290" / "who is EasyFile2290", so it answers exactly that, in the
// visible page, and the same array feeds the FAQPage markup - never one
// without the other. Rendered fully open (not an accordion) so every answer is
// in the prerendered HTML as plain readable text. Every claim is one the site
// already makes elsewhere: price = PRICE_FROM (build-checked against the live
// fee table, with free VIN corrections), authorization = IRS_EFILE.
const BRAND_FAQS = [
  {
    q: "What is EasyFile2290?",
    a: `EasyFile2290 is an online service for e-filing IRS Form 2290, the Heavy Highway Vehicle Use Tax return, at easyfile2290.com. You add your business and trucks, EasyFile2290 figures the tax from the IRS rate tables, and you e-file and get your stamped Schedule 1 back electronically.`,
  },
  {
    q: "Who runs EasyFile2290?",
    a: `EasyFile2290 is a product of ${IRS_EFILE.legalName}.`,
  },
  ...(IRS_EFILE.authorized
    ? [
        {
          q: "Is EasyFile2290 an IRS-authorized e-file provider?",
          a: `Yes. EasyFile2290 is on the IRS list of [authorized Form 2290 e-file providers](${IRS_EFILE.verifyUrl}) for tax year 2026, listed as Easyfile2290.com. Returns are transmitted directly to the IRS.`,
        },
      ]
    : []),
  {
    q: "How much does EasyFile2290 cost?",
    a: `A single-vehicle return starts at $${PRICE_FROM}, and VIN corrections are free. The fee depends on how many vehicles are on the return — the full table is on the [EasyFile2290 pricing page](/pricing).`,
  },
  {
    q: "Is EasyFile2290 the same as other \"easy 2290\" websites?",
    a: "No. EasyFile2290 — one word, at easyfile2290.com — is its own service. Other Form 2290 websites with similar names are separate companies, and we have no connection to them.",
  },
  {
    q: "How do I contact EasyFile2290?",
    a: `Email ${SUPPORT_EMAIL} or call ${SUPPORT_PHONE_DISPLAY}. More options are on the [contact page](/contact).`,
  },
];

const meta = ROUTE_META["/about"];

const About = () => (
  <div className="max-w-[860px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/about"
        keywords={meta.keywords}
        schemas={[
          aboutPageSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
          faqSchema(BRAND_FAQS),
        ]}
      />
    <PageHeader eyebrow="About" title={`About ${SITE.name}`}>
      EasyFile2290 (easyfile2290.com) is a focused tool for one job: figuring and filing the Heavy Highway Vehicle Use Tax
      (Form 2290) for the tax period beginning July 1, 2026 and ending June 30, 2027. No bundled bookkeeping, no
      unrelated modules — just the 2290 flow, done well.
    </PageHeader>

    <MarketingArt src="about.webp" className="w-full max-w-[520px] h-auto mt-7" />

    <div className="grid sm:grid-cols-3 gap-4 mt-8">
      {POINTS.map(({ icon: Icon, title, body }) => (
        <Panel key={title} padded pop>
          <div className="w-9 h-9 rounded-lg bg-[var(--accent-soft)] text-[var(--accent)] grid place-items-center mb-3">
            <Icon size={17} />
          </div>
          <b className="font-disp text-[16px]">{title}</b>
          <p className="text-[var(--muted)] text-[15px] leading-[1.6] mt-2">{body}</p>
        </Panel>
      ))}
    </div>

    <Panel padded className="mt-8">
      <h2 className="font-disp font-bold text-[16px]">One word: EasyFile2290</h2>
      <p className="text-[var(--muted)] text-[15px] leading-[1.6] mt-2">
        Our name is written as one word — EasyFile2290 — and our only website is easyfile2290.com. To find us
        again, search for <b className="text-[var(--ink)]">easyfile2290</b> or type easyfile2290.com straight into
        your browser. EasyFile2290 is a product of {IRS_EFILE.legalName}.
      </p>
    </Panel>

    <Panel padded className="mt-8">
      <b className="font-disp text-[16px]">IRS e-file authorization</b>
      <p className="text-[var(--muted)] text-[15px] leading-[1.6] mt-2">
        EasyFile2290 is an IRS-Authorized e-file Provider for Form 2290. Returns are transmitted directly to the
        IRS, and your stamped Schedule 1 comes back electronically — usually within seconds of acceptance. Tax is
        computed from the published IRS TY2026 rate tables for all 21 weight categories, including logging and
        suspended vehicles. For the official form and instructions, see IRS.gov/Form2290.
      </p>
    </Panel>

    <section className="mt-10">
      <h2 className="text-[24px] font-disp font-extrabold tracking-tight">EasyFile2290 questions</h2>
      <Panel className="mt-4 divide-y divide-[var(--line)] overflow-hidden">
        {BRAND_FAQS.map((f) => (
          <div key={f.q} className="p-[16px_18px]">
            <h3 className="font-semibold text-[15px]">{f.q}</h3>
            <p className="text-[var(--muted)] text-sm leading-[1.6] mt-1.5 max-w-[68ch]">
              <RichText text={f.a} />
            </p>
          </div>
        ))}
      </Panel>
    </section>

    <Panel padded className="!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between gap-6 mt-8 !p-[30px]">
      <div>
        <h2 className="text-[20px] font-disp">File your Form 2290 with EasyFile2290</h2>
        <p className="text-[var(--muted)] mt-1">It takes a few minutes to see your first return end to end.</p>
      </div>
      <Button as={Link} to="/login?tab=up" size="lg" className="no-underline flex-none">
        Get started free <ArrowRight size={17} />
      </Button>
    </Panel>
  </div>
);

export default About;
